import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import snipeLogo from '../../assets/snipe_logo.png';

function Discord() {
    const { user } = useAuth();

    // If Discord is already linked, redirect to manage subscriptions
    if (user?.discordLinked) {
        return (
            <div className="flex-1 flex items-center justify-center p-4 sm:p-8 overflow-auto">
                <div className="max-w-3xl w-full">
                    {/* Success State Card */}
                    <div className="relative bg-gradient-to-br from-[#0f172a] via-[#0a1628] to-[#020617] rounded-2xl border border-emerald-500/20 shadow-2xl shadow-emerald-500/5 overflow-hidden">
                        {/* Decorative Elements */}
                        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-emerald-500/5 to-teal-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
                        
                        {/* Content */}
                        <div className="relative px-8 pb-8 sm:px-12 sm:pb-12 text-center">
                            {/* Logo */}
                            <div className="mx-auto flex items-center justify-center">
                                <img 
                                    src={snipeLogo} 
                                    alt="Snipe Logo" 
                                    className="h-40 w-auto object-contain animate-fade-in"
                                />
                            </div>

                            {/* Status Badge */}
                            <div className="inline-flex items-center gap-2 bg-slate-800/50 border border-emerald-500/30 px-4 py-2 rounded-full mb-4 backdrop-blur-sm -mt-2">
                                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                                <span className="text-emerald-400 text-sm font-semibold">Connected</span>
                            </div>

                            {/* Heading */}
                            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3 flex items-center justify-center gap-3">
                                Discord Account Linked
                                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </h2>

                            {/* Description */}
                            <p className="text-slate-400 text-base sm:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
                                Your Discord account is connected to your Snipe account. Access exclusive channels and bot features based on your subscription tier. You will also have access to the Discord community.
                            </p>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                                <a
                                    href="https://discord.gg/3MBKc7yt9u"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group relative inline-flex items-center justify-center bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-4 rounded-xl font-semibold text-white transition-all duration-300 shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-[1.02] overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    <svg className="relative w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                    </svg>
                                    <span className="relative">Open Discord Server</span>
                                </a>
                                <Link
                                    to="/manage-subscriptions"
                                    className="group inline-flex items-center justify-center bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 px-8 py-4 rounded-xl font-semibold text-white hover:bg-slate-800/70 hover:border-slate-600/50 transition-all duration-300"
                                >
                                    <span>Manage Subscriptions</span>
                                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </Link>
                            </div>

                            {/* Features Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 border-t border-slate-800/50">
                                <div className="group relative bg-slate-900/30 backdrop-blur-sm rounded-xl p-5 border border-slate-800/50 hover:border-emerald-500/30 transition-all duration-300 text-center">
                                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 mx-auto">
                                        <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-white font-semibold text-sm mb-2">Exclusive Channels</h4>
                                    <p className="text-slate-500 text-xs leading-relaxed">Access tier-based channels and features</p>
                                </div>

                                <div className="group relative bg-slate-900/30 backdrop-blur-sm rounded-xl p-5 border border-slate-800/50 hover:border-emerald-500/30 transition-all duration-300 text-center">
                                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 mx-auto">
                                        <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-white font-semibold text-sm mb-2">Real-Time Alerts</h4>
                                    <p className="text-slate-500 text-xs leading-relaxed">Get instant betting opportunities</p>
                                </div>

                                <div className="group relative bg-slate-900/30 backdrop-blur-sm rounded-xl p-5 border border-slate-800/50 hover:border-emerald-500/30 transition-all duration-300 text-center">
                                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 mx-auto">
                                        <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-white font-semibold text-sm mb-2">Community</h4>
                                    <p className="text-slate-500 text-xs leading-relaxed">Connect with fellow members</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show setup instructions if Discord is not linked yet
    const steps = [
        {
            number: 1,
            title: 'Join Discord',
            description: 'Click the button below to join our Discord community where you can access the EV Bots and connect with fellow members.',
            icon: 'M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z',
            isDiscordButton: true,
        },
        {
            number: 2,
            title: 'Authenticate SnipeAIO Account',
            customDescription: (
                <>
                    Once in Discord, navigate to the <span className="text-emerald-400 font-semibold">#authenticate</span> channel and type the command:
                </>
            ),
            icon: 'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
            command: '/authenticate',
        },
        {
            number: 3,
            title: 'Confirm Email',
            customDescription: (
                <>
                    You will be prompted to enter your email address. <span className="text-emerald-400 font-semibold">You must use the same email address you used to sign up to SnipeAIO</span> for verification.
                </>
            ),
            icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
        },
        {
            number: 4,
            title: 'Access Granted',
            description: 'Once authenticated, you will automatically be assigned the necessary roles to access all EV Bot channels based on your subscription tier.',
            icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
        },
        {
            number: 5,
            title: 'Manage Subscription',
            description: 'View and manage your subscription, upgrade or downgrade your plan, and access billing history.',
            icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
            link: '/manage-subscriptions',
            linkText: 'Manage Subscriptions',
        },
    ];

    return (
        <div className="flex-1 p-8 overflow-auto">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                        Join Our Discord Community
                    </h1>
                    <p className="text-slate-400 text-lg">
                        Follow these steps to get started with the NBA EV Bot
                    </p>
                </div>

                {/* Steps */}
                <div className="space-y-6">
                    {steps.map((step, index) => (
                        <div
                            key={step.number}
                            className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl border border-slate-800/50 p-6 hover:border-slate-700/50 transition-all duration-300"
                        >
                            {/* Step Header */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-emerald-500/20">
                                        {step.number}
                                    </div>
                                    <h3 className="text-xl font-semibold text-white">
                                        {step.title}
                                    </h3>
                                </div>
                                <div className="w-10 h-10 bg-slate-800/50 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={step.icon} />
                                    </svg>
                                </div>
                            </div>

                            {/* Step Content */}
                            <div>
                                <p className="text-slate-400 leading-relaxed mb-4">
                                    {step.customDescription || step.description}
                                </p>

                                {/* Command Display */}
                                {step.command && (
                                    <div className="bg-slate-900/50 border border-slate-700/50 rounded-lg px-4 py-3">
                                        <code className="text-emerald-400 font-mono text-sm">
                                            {step.command}
                                        </code>
                                    </div>
                                )}

                                {/* Link to Manage Subscriptions */}
                                {step.link && (
                                    <Link
                                        to={step.link}
                                        className="inline-flex items-center text-emerald-400 hover:text-emerald-300 transition-colors duration-200 text-sm font-medium"
                                    >
                                        {step.linkText}
                                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </Link>
                                )}

                                {/* Discord Join Button */}
                                {step.isDiscordButton && (
                                    <a
                                        href="https://discord.gg/3MBKc7yt9u"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-3 rounded-lg font-semibold text-white hover:from-emerald-700 hover:to-teal-700 transition duration-200 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/40 hover:scale-[1.02] transform"
                                    >
                                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                        </svg>
                                        Join Discord Server
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Help Section */}
                <div className="mt-12 bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl border border-slate-800/50 p-6 text-center">
                    <h3 className="text-lg font-semibold text-white mb-2">Need Help?</h3>
                    <p className="text-slate-400 text-sm">
                        If you encounter any issues during the setup process, please reach out to our support team in Discord or email support at{' '}
                        <a href="mailto:info@snipeaio.com" className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200">
                            info@snipeaio.com
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Discord;

